import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageSeo, SITE_ORIGIN } from "@/hooks/usePageSeo";
import { getArticleBySlug, getCategoryName, getRelatedArticles } from "@/mocks/articles";
import ArticleBody, { buildToc } from "./components/ArticleBody";
import ArticleSidebar from "./components/ArticleSidebar";

export default function ArticleDetail() {
  const { slug = "" } = useParams();
  const article = getArticleBySlug(slug);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toc = useMemo(() => (article ? buildToc(article.content) : []), [article]);

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          dateModified: article.date,
          inLanguage: "zh-CN",
          articleSection: getCategoryName(article.categoryId),
          keywords: article.tags.join(","),
          author: { "@type": "Person", name: article.author },
          publisher: { "@type": "Organization", name: "诱惑吃瓜", url: SITE_ORIGIN },
          mainEntityOfPage: `${SITE_ORIGIN}/article/${article.slug}`,
          image: article.cover,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
            { "@type": "ListItem", position: 2, name: "热瓜榜", item: `${SITE_ORIGIN}/hot` },
            { "@type": "ListItem", position: 3, name: article.title },
          ],
        },
      ],
    };
  }, [article]);

  usePageSeo({
    title: article ? `${article.title} | 诱惑吃瓜` : "内容未找到 | 诱惑吃瓜",
    description: article ? article.excerpt : "该瓜线内容暂未找到，返回热瓜榜看看其他正在发酵的话题。",
    keywords: article ? `${article.tags.join(",")},诱惑吃瓜` : "诱惑吃瓜,热瓜榜",
    path: `/article/${slug}`,
    type: "article",
    jsonLd,
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16 md:pt-20 flex items-center justify-center px-4">
          <div className="text-center py-24">
            <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-2xl text-foreground-400 mx-auto">
              <i className="ri-emotion-sad-line w-8 h-8 flex items-center justify-center"></i>
            </span>
            <h1 className="mt-5 text-2xl font-bold text-foreground-950">这条瓜线暂时找不到</h1>
            <p className="mt-3 text-sm text-foreground-500">可能已经被合并到其他话题里了。</p>
            <Link
              to="/hot"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回热瓜榜
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedArticles(article.slug, 3);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-20">
        <div className="w-full px-4 md:px-10 pt-8 md:pt-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "热瓜榜", to: "/hot" },
              { label: getCategoryName(article.categoryId), to: `/hot?cat=${article.categoryId}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-10 pt-6 pb-16 md:pb-24">
          <header className="max-w-4xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
              <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center"></i>
              {getCategoryName(article.categoryId)}
            </span>
            <h1 className="mt-4 text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              {article.title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line w-4 h-4 flex items-center justify-center"></i>
                {article.author} · {article.authorRole}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                发布于 {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                {(article.views / 1000).toFixed(1)}k 阅读
              </span>
            </div>
          </header>

          <div className="mt-8 rounded-lg overflow-hidden bg-background-200 aspect-[16/9] max-h-[520px]">
            <img
              src={article.cover}
              alt={`${article.title} — 诱惑吃瓜话题配图`}
              title={`${article.title} 专题图片`}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="mt-10">
            <ArticleBody blocks={article.content} toc={toc} />
          </div>

          <div className="mt-10 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/hot?q=${encodeURIComponent(tag)}`}
                className="px-3.5 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-xs hover:bg-secondary-200 transition-colors"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <div className="mt-12">
            <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground-950">还没聊够？</h2>
                <p className="mt-2 text-sm text-foreground-600">
                  去聊天室蹲后续，和大家一起等下一次更新。
                </p>
              </div>
              <Link
                to="/chat"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-chat-smile-2-line w-4 h-4 flex items-center justify-center"></i>
                进聊天室
              </Link>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-8">
              <h2 className="text-xl font-bold text-foreground-950 mb-5">继续围观</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {related.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/article/${item.slug}`}
                    className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors"
                  >
                    <span className="block h-28 bg-background-200 overflow-hidden">
                      <img
                        src={item.cover}
                        alt={`${item.title} 配图`}
                        title={`${item.title} — 诱惑吃瓜`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </span>
                    <span className="block p-3.5">
                      <span className="block text-xs text-foreground-500">
                        {getCategoryName(item.categoryId)}
                      </span>
                      <span className="mt-1 block text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                        {item.title}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <div className="lg:col-span-4">
              <ArticleSidebar />
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {showTop ? (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="返回顶部"
          className="fixed bottom-6 right-6 z-40 w-11 h-11 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-lg hover:bg-primary-600 transition-colors"
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      ) : null}
    </div>
  );
}